import { Link } from 'react-router-dom';

import './pages.css';

const TITLE = 'What is DAP & DAP-AI?';

type Pillar = {
  title: string;
  description: string;
  color: string;
  tint: string;
  path: string;
};

const PILLARS: Pillar[] = [
  {
    title: 'Data Intelligence',
    description:
      'Leveraging intelligent analysis and mining of supplier quality data—to transform raw data into actionable insights that enable quality risk early warning and supplier performance optimization.',
    color: '#4f46e5',
    tint: '#e7e6fb',
    path: '/portal/data-intelligence',
  },
  {
    title: 'Automation',
    description:
      'Leveraging AI-driven automation to streamline daily workflows, thereby boosting work efficiency and saving time.',
    color: '#7c3aed',
    tint: '#efe6fb',
    path: '/portal/automation',
  },
  {
    title: 'SQM Expert',
    description:
      'An intelligent Q&A chatbot that serves as a knowledge expert across all SQM commodities, providing instant access to institutional knowledge and eliminating human bias in information retrieval.',
    color: '#8b5cf6',
    tint: '#f1ecfc',
    path: '/portal/sqm-expert',
  },
  {
    title: 'Readiness Review',
    description:
      'Our latest state of the intelligent Readiness Review function with AI. Connects disparate product data from design, manufacturing, and the field to proactively identify potential quality issues, accelerate root cause analysis, and provide actionable Go/No-Go recommendations.',
    color: '#a855f7',
    tint: '#f6ebfd',
    path: '/portal/sqm-consultant',
  },
  {
    title: 'Remote Supplier Process Control',
    description:
      'Real-time data connectivity with supplier factory databases to continuously monitor production processes, automatically analyzing IQC, IPQC, and OQC inspection data along with process parameters.',
    color: '#c026d3',
    tint: '#fbe9fb',
    path: '/portal/supplier-process-control',
  },
];

type GalleryItem =
  | {
      kind: 'mission';
      size: 'lg' | 'md' | 'sm' | 'wide' | 'tall';
    }
  | {
      kind: 'placeholder';
      label: string;
      size: 'lg' | 'md' | 'sm' | 'wide' | 'tall';
      image?: string;
    };

const GALLERY: GalleryItem[] = [
  { kind: 'mission', size: 'tall' },
  {
    kind: 'placeholder',
    label: 'Image slot 1',
    size: 'tall',
    image: '/gallery/slot1.png',
  },
  {
    kind: 'placeholder',
    label: 'Image slot 2',
    size: 'tall',
    image: '/gallery/slot2.png',
  },
  {
    kind: 'placeholder',
    label: 'Image slot 3',
    size: 'tall',
    image: '/gallery/slot3.png',
  },
];

const MISSION_ITEMS = [
  'Strive to develop an AI tool to be fields of expertise — answers as specific and useful as possible',
  'Faster and more robust decision making',
  'Improve project quality management and supplier management efficiency and accuracy',
];

export function OverviewPage() {
  return (
    <div className="page overview-hero">
      <h1 className="overview-hero__title" aria-label={TITLE}>
        {TITLE.split('').map((char, index) => (
          <span
            key={index}
            className="overview-hero__char"
            aria-hidden="true"
            style={{ animationDelay: `${index * 0.08}s` }}
          >
            {char === ' ' ? '\u00A0' : char}
          </span>
        ))}
      </h1>

      <div className="pillars">
        {PILLARS.map((pillar) => (
          <Link className="pillar" key={pillar.title} to={pillar.path}>
            <div
              className="pillar__banner"
              style={{ background: pillar.color }}
            >
              <span className="pillar__title">{pillar.title}</span>
            </div>
            <div
              className="pillar__body"
              style={{ background: pillar.tint }}
            >
              <p className="pillar__desc">{pillar.description}</p>
            </div>
          </Link>
        ))}
      </div>

      <section className="gallery" aria-label="DAP-AI showcase">
        <div className="gallery__grid">
          {GALLERY.map((item, index) => (
            <div
              key={item.kind === 'mission' ? 'mission' : item.label}
              className={`gallery__card gallery__card--${item.size} ${
                item.kind === 'mission'
                  ? 'gallery__card--mission'
                  : item.image
                    ? 'gallery__card--image'
                    : 'gallery__card--placeholder'
              }`}
              style={{ animationDelay: `${index * 0.12}s` }}
            >
              {item.kind === 'mission' ? (
                <>
                  <span className="gallery__eyebrow">Our Mission</span>
                  <ul className="gallery__mission-list">
                    {MISSION_ITEMS.map((m) => (
                      <li key={m}>{m}</li>
                    ))}
                  </ul>
                </>
              ) : item.image ? (
                <img
                  className="gallery__image"
                  src={item.image}
                  alt={item.label}
                />
              ) : (
                <div className="gallery__placeholder">
                  <svg
                    className="gallery__placeholder-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <rect
                      x="3"
                      y="3"
                      width="18"
                      height="18"
                      rx="2"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    />
                    <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" />
                    <path
                      d="M21 15l-5-5L5 21"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span className="gallery__placeholder-label">
                    {item.label}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
